import React, {
    FC,
    useState,
    useEffect,
    Dispatch,
    SetStateAction,
    useRef
} from 'react'
// @ts-ignore
import s from '@components/common/Navbar/Navbar.module.scss'
// @ts-ignore
import { UserNav } from '@components/common/UserNav/UserNav'
import { useRouter } from 'next/router'
import cn from 'classnames'
import throttle from 'lodash.throttle'
import { MegaMenu } from '@components/alfakher/pylot-megamenu/src/MegaMenu'
import { useTranslation } from 'next-i18next'
// @ts-ignore
import LocationModal from '@ooka_default/components/common/LocationModal'
import { usePreloadedData } from '@preload'
import { PromoBanner } from '@corratech/pylot-promobanner'
import { RichContent } from '@corratech/pylot-rich-content'
import { useIsMobile } from '@components/common/IsMobile/useIsMobile'
import { useAlfakherContext } from '@components/alfakherContext/AlfakherContext'
import Link from '@corratech/pylot-ui/src/Link'
import Logo from '@components/icons/Logo'
import { useUI } from '@corratech/pylot-ui/context'

/**
 * Is used to query element by id and get navbar's height as an offset
 * when scrolling to the target element.
 */
export const NAVBAR_ID = 'navbar'
const SHOW_CURRENCY = false

type cmsBlocks = {
    identifier: string
    title: string
    content: string
}

type CmsBlocksResponse = {
    data: {
        cmsBlocks: {
            items: cmsBlocks[] | [null]
        }
    }
}

const Navbar: FC<{
    displaySearch: boolean
    setIsPromoCookieCleared?: Dispatch<SetStateAction<boolean>>
    isPromoCookieCleared?: boolean
    isNicotinePage?: boolean
    bundleStep: number
    setBundleStep: (step: number) => void
}> = ({
    setIsPromoCookieCleared,
    isNicotinePage,
    bundleStep,
    setBundleStep
}) => {
    const [hasScrolled, setHasScrolled] = useState(false)
    const [heroVisible, setHeroVisible] = useState(false)
    const [scrollDirection, setScrollDirection] = useState('')
    const navWrapperRef = useRef<HTMLDivElement>(null)
    const { t } = useTranslation('common')
    const router = useRouter()
    const { asPath, back } = router
    const {
        // @ts-ignore
        showLocationModal,
        // @ts-ignore
        setShowLocationModal
    } = useAlfakherContext()
    const initialData = (usePreloadedData(
        'header'
    ) as unknown) as CmsBlocksResponse
    const isMobile = useIsMobile()
    const { scrollNavbar } = useUI()
    const items = initialData?.data?.cmsBlocks?.items ?? []

    useEffect(() => {
        const layoutClass = document.getElementById('layout-main')
        if (layoutClass) {
            if (asPath === '/') {
                layoutClass.classList.add('ookaHome')
                layoutClass.classList.remove('ookaCategory')
            } else {
                layoutClass.classList.remove('ookaHome')
                layoutClass.classList.remove('ookaCategory')
            }
        }
    }, [asPath])

    useEffect(() => {
        const isHelpAndFaqsPage = asPath.includes('help-and-faqs')
        if (isHelpAndFaqsPage) {
            const faqPageTabsWrapper: HTMLElement = document.querySelector(
                '.faq-page-tabs > div'
            )!
            if (!faqPageTabsWrapper || !isMobile) return
            const tabsOffset = navWrapperRef.current?.getBoundingClientRect()
                ?.height
            if (scrollDirection === 'up') {
                faqPageTabsWrapper.style.top = `${tabsOffset}px`
            } else {
                faqPageTabsWrapper.style.top = '0'
            }
        }
    }, [asPath, isMobile, scrollDirection])

    useEffect(() => {
        let lastScrollY = window.scrollY
        const HERO_END_MARKER_SELECTOR = 'video_section_home'
        const NAVBAR_HEIGHT_OFFSET = 130
        const handleScroll = throttle(() => {
            // function to run on scroll

            const scrollY = window.scrollY
            const direction = scrollY > lastScrollY ? 'down' : 'up'
            if (direction !== scrollDirection) {
                setScrollDirection(direction)
            }
            lastScrollY = scrollY > 0 ? scrollY : 0
            const targetEl = document.getElementsByClassName(
                HERO_END_MARKER_SELECTOR
            )[0]
            if (targetEl) {
                const rect = targetEl.getBoundingClientRect()
                const isVisible =
                    rect.bottom - NAVBAR_HEIGHT_OFFSET <
                    document.documentElement.clientHeight
                setHeroVisible(isVisible)
            }
            const offset = 0
            const { scrollTop } = document.documentElement
            const scrolled = scrollTop > offset
            if (!targetEl) {
                setHeroVisible(scrolled)
            }
            setHasScrolled(scrolled)
            scrollNavbar(scrolled)
        }, 200)
        if (!items[0] && setIsPromoCookieCleared) setIsPromoCookieCleared(false)

        document.addEventListener('scroll', handleScroll)
        return () => {
            document.removeEventListener('scroll', handleScroll)
        }
    }, [])

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const handleChatOpen = () => {}

    const renderLogoAndNavigation = () => (
        <Link href="/">
            <span className={s.logo} aria-label={t('Logo')}>
                <Logo />
                <span className="sr-only">{t('Logo')}</span>
            </span>
        </Link>
    )

    const isCustomkit = router.query.option === 'customize'

    return (
        <>
            <LocationModal
                setShowLocationModal={setShowLocationModal}
                showLocationModal={showLocationModal}
            />
            <div
                ref={navWrapperRef}
                className={cn(
                    s.root,
                    s['fixed-search'],
                    'header-parent',
                    {
                        blackHeaderOnScroll: heroVisible
                    },
                    [
                        isMobile && scrollDirection === 'down'
                            ? s['HeaderOnScroll']
                            : s['scrollHeaderUp']
                    ]
                )}
            >
                <div>
                    {items[1] && isNicotinePage && (
                        <div className={s['legal-top-banner']}>
                            <RichContent html={items[1]?.content} />
                        </div>
                    )}
                    {items[0] && (
                        <PromoBanner
                            className={cn(s['promo-banner'], 'promo-banner')}
                            cookieName="promobanner"
                        >
                            <RichContent html={items[0]?.content} />
                        </PromoBanner>
                    )}
                </div>
                <div className={s['header-main']}>
                    <div
                        className={cn(
                            `${s['top-header-section']} ${
                                isCustomkit
                                    ? s['custom-top-header-section']
                                    : ''
                            }`,
                            'top-header-section'
                        )}
                    >
                        {isCustomkit ? (
                            <>
                                <span
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={() => {
                                        if (bundleStep > 0) {
                                            return setBundleStep(bundleStep - 1)
                                        }
                                        back()
                                    }}
                                    onClick={() => {
                                        if (bundleStep > 0) {
                                            return setBundleStep(bundleStep - 1)
                                        }
                                        back()
                                    }}
                                    className={s['back-arrow']}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        fill="none"
                                    >
                                        <path
                                            d="M15 18l-6-6 6-6"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            fill="none"
                                        />
                                    </svg>
                                </span>
                                <div className={s['top-header-logo-custom']}>
                                    {renderLogoAndNavigation()}
                                </div>
                            </>
                        ) : (
                            <MegaMenu
                                className={cn(
                                    'megamenu-cms',
                                    !hasScrolled && 'megamenu-not-sticky'
                                )}
                                showCurrency={SHOW_CURRENCY}
                                handleChatOpen={handleChatOpen}
                            />
                        )}
                        <div
                            className={`${s['top-header-right-section']} ${
                                isCustomkit ? s['custom-kit-right-section'] : ''
                            }`}
                        >
                            <UserNav showCurrency={SHOW_CURRENCY} />
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Navbar
